import { CommonModule } from '@angular/common';
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  AlertDialogComponent,
  DialogOption as AlertDialogOption,
} from '@components/shared/alert-dialog/alert-dialog.component';
import { MinutesLabelPipe } from '@pipes/minutes-label.pipe';
import {
  CreateQTSFormGroup,
  DeviceTypeValidatorViewModel,
} from '@services/qts-formgroup';
import {
  DeviceSettingBase,
  resetSensorSettings,
  sensorsHavingForm,
} from '@utils/device-setting.util';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';

/** ダイアログのViewModel */
export type DeviceGroupDialogOption = {
  updating: boolean;
  deviceSettingGroupId: string;
  deviceSettingGroupName: string;
  deviceTypeId: string;
  assettypeList?: AssettypeViewModel[];
  deviceType: DeviceTypeViewModel;
  contractExpiredAt: string;
  target?: DeviceGroupSettingViewModel;
};

/** アセットタイプ */
export type AssettypeViewModel = DeviceSettingBase & {
  /** アセットタイプID */
  assettypeId: string;
  /** アセットタイプ名 */
  assettypeName: string;
  /** テナントID */
  tenantId: string;
  /** デバイスステータス */
  deviceActiveStatus: boolean;
  /** デバイス種類ID */
  deviceTypeId: string;
  /** センサーデータ収集間隔 */
  samplingFrequency: number | null;
  /** クラウドへのデータ送信間隔 */
  reportingFrequency: number | null;
  /** 作成者 */
  createdBy: string;
  /** 更新者 */
  updatedBy: string;
  /** 作成日時 */
  createdAt: Date;
  /** 更新日時 */
  updatedAt: Date;
};
/** デバイス設定のViewModel */
export type DeviceGroupSettingViewModel = DeviceSettingBase & {
  /** デバイスグループ名 */
  deviceSettingGroupName?: string;
  /** デバイスグループID */
  deviceSettingGroupId?: string;
  /** デバイス種類ID */
  deviceTypeId?: string;
  /** センサーデータ収集間隔 */
  samplingFrequency: number | null;
  /** クラウドへのデータ送信間隔 */
  reportingFrequency: number | null;
  /** 全デバイス契約終了 */
  allExpired: boolean;
  /** フライトモード */
  fmd?: boolean;
};

/** デバイス種類のViewModel */
export type DeviceTypeViewModel = {
  /** デバイス種類ID */
  deviceTypeId: string;
  /** 説明文 */
  description?: string;
  /** センサーデータ収集間隔 */
  samplingFrequency?: number[];
  /** クラウドデータ送信間隔 */
  reportingFrequency?: number[];
  /** 温度センサー */
  temperatureSensor?: boolean;
  /** 気圧センサー */
  pressureSensor?: boolean;
  /** 湿度センサー */
  humiditySensor?: boolean;
  /** 傾斜センサー */
  tiltSensor?: boolean;
  /** 照度センサー */
  lightSensor?: boolean;
  /** 位置測位 */
  locationSensor?: boolean;
  /** 位置測位_GNSS */
  locationTechGnss?: boolean;
  /** 位置測位_Wi-Fi */
  locationTechWifi?: boolean;
  /** 位置測位_Cellular */
  locationTechCellular?: boolean;
  /** 落下イベント検知 */
  fallDetected?: boolean;
  /** 動作イベント検知 */
  mcdDetected?: boolean;
  /** 明暗イベント検知 */
  lightExposureDetected?: boolean;
  /** 衝撃イベント検知 */
  shockDetected?: boolean;
  /** フライトモード */
  fmd?: boolean;
};

/** フォームグループ */
type DeviceSettingFormGroup = FormGroup<{
  /** デバイスグルーID名 */
  deviceSettingGroupId: FormControl<string>;
  /** デバイスグループ名 */
  deviceSettingGroupName: FormControl<string>;
  /** センサーデータ収集期間 */
  samplingFrequency: FormControl<number | null>;
  /** クラウドデータ収集期間 */
  reportingFrequency: FormControl<number | null>;
  /** 位置検知 */
  locationDetected: FormControl<boolean>;
  /** 位置測位技術優先度 */
  locationPriority: FormGroup<{
    locationPriority1: FormControl<string | null>;
    locationPriority2: FormControl<string | null>;
    locationPriority3: FormControl<string | null>;
  }>;
  /** 落下検知 */
  fallDetected: FormControl<boolean>;
  /** 動作検知 */
  mcdDetected: FormControl<boolean>;
  /** 明暗検知 */
  lightExposureDetected: FormControl<boolean>;
  /** 明暗検知設定値 */
  lightExposureDetectValue: FormControl<string>;
  /** 衝撃検知 */
  shockDetected: FormControl<boolean>;
  /** 衝撃検知設定値 */
  shockDetectedDetectValue: FormControl<number>;
  /** 温度センサー */
  temperature: FormControl<boolean>;
  /** 温度センサー設定 */
  temperatureSettings: FormGroup<{
    /** 温度センサー：上限逸脱 */
    temperatureAlertMax: FormControl<number>;
    /** 温度センサー：上限警告 */
    temperaturePrealertMax: FormControl<number>;
    /** 温度センサー：下限警告 */
    temperaturePrealertMin: FormControl<number>;
    /** 温度センサー：下限逸脱 */
    temperatureAlertMin: FormControl<number>;
  }>;
  /** 気圧センサー */
  pressure: FormControl<boolean>;
  /** 気圧センサー設定 */
  pressureSettings: FormGroup<{
    /** 気圧センサー：上限逸脱 */
    pressureAlertMax: FormControl<number>;
    /** 気圧センサー：上限警告 */
    pressurePrealertMax: FormControl<number>;
    /** 気圧センサー：下限警告 */
    pressurePrealertMin: FormControl<number>;
    /** 気圧センサー：下限逸脱 */
    pressureAlertMin: FormControl<number>;
  }>;
  /** 湿度センサー */
  humidity: FormControl<boolean>;
  /** 湿度センサー設定 */
  humiditySettings: FormGroup<{
    /** 湿度センサー：上限逸脱 */
    humidityAlertMax: FormControl<number>;
    /** 湿度センサー：上限警告 */
    humidityPrealertMax: FormControl<number>;
    /** 湿度センサー：下限警告 */
    humidityPrealertMin: FormControl<number>;
    /** 湿度センサー：下限逸脱 */
    humidityAlertMin: FormControl<number>;
  }>;
  /** 傾斜センサー */
  tilt: FormControl<boolean>;
  /** 傾斜センサー設定 */
  tiltSettings: FormGroup<{
    /** 傾斜センサー：上限逸脱 */
    tiltAlertMax: FormControl<number>;
    /** 傾斜センサー：上限警告 */
    tiltPrealertMax: FormControl<number>;
    /** 傾斜センサー：下限警告 */
    tiltPrealertMin: FormControl<number>;
    /** 傾斜センサー：下限逸脱 */
    tiltAlertMin: FormControl<number>;
  }>;
  /** 照度センサー */
  light: FormControl<boolean>;
  /** 照度センサー設定 */
  lightSettings: FormGroup<{
    /** 照度センサー：上限逸脱 */
    lightAlertMax: FormControl<number>;
    /** 照度センサー：上限警告 */
    lightPrealertMax: FormControl<number>;
    /** 照度センサー：下限警告 */
    lightPrealertMin: FormControl<number>;
    /** 照度センサー：下限逸脱 */
    lightAlertMin: FormControl<number>;
  }>;

  /** フライトモード */
  fmd: FormControl<boolean>;

  /** 配送機能での利用許可 */
  shipmentTracking: FormControl<boolean>;
}>;

@Component({
  selector: 'app-asset-type-edit-dialog',
  standalone: true,
  imports: [
    FormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    ReactiveFormsModule,
    MatFormFieldModule,
    CommonModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatRadioModule,
    MatIcon,
    MatTooltipModule,
    MatIconModule,
    MatButtonModule,
    MinutesLabelPipe,
  ],
  templateUrl: './device-group-detail-edit-dialog.component.html',
  styleUrl: './device-group-detail-edit-dialog.component.scss',
})
export class DeviceGroupDetailEditDialogComponent
  implements AfterViewInit, AfterViewChecked, OnInit, OnDestroy
{
  /** 更新中 */
  public updating: boolean;

  /** デバイスグループID */
  public deviceSettingGroupId: string;

  /** デバイスグループ名 */
  public deviceSettingGroupName: string;

  /** デバイス設定詳細初期表示値 */
  public initDeviceGroupSetting: DeviceGroupSettingViewModel;

  /** デバイス設定詳細初期表示値（DeviceSettingBaseの型に合わせたもの） */
  public initAssettype: DeviceSettingBase;

  /** アセットタイプ設定時、トグルOFF時の変更先の設定値 */
  public assettypeRestoreValue: DeviceSettingBase;

  /** アセットタイプ一覧 */
  public assettypeList: AssettypeViewModel[];

  /** デバイス種類 */
  public deviceType: DeviceTypeViewModel;

  /** フォームグループ */
  public formGroup: FormGroup;

  /** QTS110用デバイス種類 */
  public deviceTypeQTS110?: DeviceTypeValidatorViewModel;

  /** 測定間隔リスト */
  public samplingFrequencies: number[] = [];

  /** 送信間隔リスト */
  public reportingFrequencies: number[] = [];

  /** フィルター済み送信間隔リスト */
  public filteredReportingFrequencies: number[] = [];

  /** 位置測位技術優先度のリスト */
  public locationPriorities: { [key: string]: string } =
    this.createFormGroup.locationPriorities;

  /** 位置測位技術優先度のドロップダウン表示数 */
  public locationPrioritiesNum: number;

  /** 明暗検知設定値のリスト */
  public lightExposureDetectValues: { [key: string]: string } =
    this.createFormGroup.defaultLightExposureDetectValues;

  /** 衝撃検知設定値のリスト */
  public shockDetectedDetectValues: number[] =
    this.createFormGroup.defaultShockDetectedDetectValues;

  /** デバイス名変更有効・無効状態 */
  public deviceSettingGroupNameEdit = false;

  /** デバイス種類ID */
  public deviceTypeId: string = '';

  /** デバイス契約終了日 */
  private contractExpiredAt: string;

  /** ツールチップメッセージ */
  public tooltipMessage: string;

  /** コンポーネント破棄時にObservableを解放するためのSubject */
  private destroy$ = new Subject<void>();

  /** デバイス名入力フォーム*/
  @ViewChild('deviceNameInput') deviceNameInputElement!: ElementRef;

  constructor(
    @Inject(MAT_DIALOG_DATA) options: DeviceGroupDialogOption,
    private dialogRef: MatDialogRef<DeviceSettingFormGroup>,
    private dialog: MatDialog,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private createFormGroup: CreateQTSFormGroup,
  ) {
    this.updating = options.updating;
    this.deviceSettingGroupId = options.deviceSettingGroupId;
    this.deviceSettingGroupName = options.deviceSettingGroupName;
    this.deviceTypeId = options.deviceTypeId;
    this.assettypeList = options.assettypeList || [];
    this.deviceType = options.deviceType;
    this.contractExpiredAt = options.contractExpiredAt;
    this.deviceTypeQTS110 = options.deviceType;
    this.initDeviceGroupSetting = {
      deviceSettingGroupName: options.target?.deviceSettingGroupName,
      deviceSettingGroupId: options.target?.deviceSettingGroupId,
      deviceTypeId: options.target?.deviceTypeId,
      samplingFrequency: options.target?.samplingFrequency || null,
      reportingFrequency: options.target?.reportingFrequency || null,
      shipmentTracking: options.target?.shipmentTracking || false,
      locationDetected: options.target?.locationDetected || false,
      locationPriority: options.target?.locationPriority,
      fallDetected: options.target?.fallDetected || false,
      mcdDetected: options.target?.mcdDetected || false,
      lightExposureDetected: options.target?.lightExposureDetected || false,
      lightExposureDetectValue: options.target?.lightExposureDetectValue || '',
      shockDetected: options.target?.shockDetected || false,
      shockDetectedDetectValue: options.target?.shockDetectedDetectValue || 5,
      temperature: options.target?.temperature || false,
      temperatureSettings: options.target?.temperatureSettings,
      pressure: options.target?.pressure || false,
      pressureSettings: options.target?.pressureSettings,
      humidity: options.target?.humidity || false,
      humiditySettings: options.target?.humiditySettings,
      tilt: options.target?.tilt || false,
      tiltSettings: options.target?.tiltSettings,
      light: options.target?.light || false,
      lightSettings: options.target?.lightSettings,
      allExpired: options.target?.allExpired || false,
      fmd: options.target?.fmd || false,
    };

    this.initAssettype = {
      shipmentTracking: this.initDeviceGroupSetting.shipmentTracking,
      locationDetected: this.initDeviceGroupSetting.locationDetected || false,
      locationPriority: this.initDeviceGroupSetting.locationPriority,
      fallDetected: this.initDeviceGroupSetting.fallDetected || false,
      mcdDetected: this.initDeviceGroupSetting.mcdDetected || false,
      lightExposureDetected:
        this.initDeviceGroupSetting.lightExposureDetected || false,
      lightExposureDetectValue:
        this.initDeviceGroupSetting.lightExposureDetectValue || '',
      shockDetected: this.initDeviceGroupSetting.shockDetected || false,
      shockDetectedDetectValue:
        this.initDeviceGroupSetting.shockDetectedDetectValue || 0,
      temperature: this.initDeviceGroupSetting.temperature || false,
      temperatureSettings: this.initDeviceGroupSetting.temperatureSettings,
      pressure: this.initDeviceGroupSetting.pressure || false,
      pressureSettings: this.initDeviceGroupSetting.pressureSettings,
      humidity: this.initDeviceGroupSetting.humidity || false,
      humiditySettings: this.initDeviceGroupSetting.humiditySettings,
      tilt: this.initDeviceGroupSetting.tilt || false,
      tiltSettings: this.initDeviceGroupSetting.tiltSettings,
      light: this.initDeviceGroupSetting.light || false,
      lightSettings: this.initDeviceGroupSetting.lightSettings,
    };

    this.assettypeRestoreValue = this.initAssettype;

    const emptyAssettype: AssettypeViewModel = {
      assettypeId: '',
      assettypeName: '',
      tenantId: '',
      deviceActiveStatus: false,
      deviceTypeId: '',
      samplingFrequency: null,
      reportingFrequency: null,
      shipmentTracking: false,
      locationDetected: false,
      locationPriority: [],
      fallDetected: false,
      mcdDetected: false,
      lightExposureDetected: false,
      lightExposureDetectValue: '',
      shockDetected: false,
      shockDetectedDetectValue: 0,
      temperature: false,
      temperatureSettings: [],
      pressure: false,
      pressureSettings: [],
      humidity: false,
      humiditySettings: [],
      tilt: false,
      tiltSettings: [],
      light: false,
      lightSettings: [],
      createdBy: '',
      updatedBy: '',
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    this.assettypeList.unshift(emptyAssettype);

    this.locationPriorities = this.createFormGroup.updateLocationPriorities(
      this.deviceType?.locationTechCellular,
      this.deviceType?.locationTechGnss,
      this.deviceType?.locationTechWifi,
    );

    this.locationPrioritiesNum = Object.keys(this.locationPriorities).length;

    this.formGroup = this.createFormGroup.createQTSFormGroup(
      options.target,
      this.deviceTypeQTS110,
    );

    // [#159]端末側の不具合のため、動作は設定不可とする
    this.formGroup.controls['mcdDetected'].setValue(false, {
      emitEvent: false,
    });
    this.formGroup.controls['mcdDetected'].disable();

    this.formGroup.addControl(
      'deviceSettingGroupName',
      new FormControl(this.deviceSettingGroupName || '', {
        nonNullable: true,
        validators: [Validators.maxLength(50)],
      }),
    );

    this.formGroup.addControl(
      'shipmentTracking',
      new FormControl<boolean>(options.target?.shipmentTracking || false, {
        nonNullable: true,
      }),
    );

    this.tooltipMessage =
      'ON：配送設定での利用を許可する　\n' +
      'OFF：配送設定での利用を許可しない';

    if (this.deviceType?.samplingFrequency) {
      this.samplingFrequencies = this.deviceType?.samplingFrequency;
    }
    if (this.deviceType?.reportingFrequency) {
      this.reportingFrequencies = this.deviceType.reportingFrequency;
      this.filteredReportingFrequencies = this.deviceType.reportingFrequency;
    }

    // 全デバイスが契約終了の場合、コンポーネントを非活性にしたうえでメッセージを表示する。
    if (options.target?.allExpired) {
      this.formGroup.disable();
    }
  }

  ngOnInit(): void {
    // 各センサーのトグル監視をまとめて登録
    sensorsHavingForm.forEach((sensor) => this.watchSensorToggle(sensor));
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewChecked(): void {
    this.changeDetectorRef.detectChanges();
  }

  ngAfterViewInit(): void {
    this.createFormGroup.subscribeFormChanges(this.formGroup);
  }

  /**
   * 送信
   */
  public async submit() {
    const deviceSettingGroupName =
      this.formGroup.get('deviceSettingGroupName')?.value ||
      this.deviceSettingGroupName;
    const operation = this.updating ? '変更' : '新規登録';
    const message = `設定グループ「${deviceSettingGroupName}」に所属するデバイスの設定を${operation}してよろしいですか？\n使用中のデバイス含め、全てのデバイスの設定が変更になります。`;
    const options: AlertDialogOption = {
      type: 'warn',
      title: 'Confirm',
      message: message,
      operation: operation,
    };

    const result: boolean = await firstValueFrom(
      this.dialog
        .open(AlertDialogComponent, {
          width: '600px',
          data: options,
        })
        .afterClosed(),
    );

    if (!result) {
      return;
    }

    const deviceSetting: DeviceGroupSettingViewModel = {
      deviceSettingGroupId:
        this.formGroup.get('deviceSettingGroupId')?.value || '',
      deviceSettingGroupName:
        this.formGroup.get('deviceSettingGroupName')?.value || '',
      deviceTypeId: this.deviceTypeId,
      samplingFrequency: this.formGroup.get('samplingFrequency')?.value || null,
      reportingFrequency:
        this.formGroup.get('reportingFrequency')?.value || null,
      shipmentTracking: this.formGroup.get('shipmentTracking')?.value || false,
      locationDetected: this.formGroup.get('locationDetected')?.value || false,
      locationPriority: [
        this.formGroup.get('locationPriority')?.get('locationPriority1')
          ?.value || '',
        this.formGroup.get('locationPriority')?.get('locationPriority2')
          ?.value || '',
        this.formGroup.get('locationPriority')?.get('locationPriority3')
          ?.value || '',
      ],
      fallDetected: this.formGroup.get('fallDetected')?.value || false,
      mcdDetected: this.formGroup.get('mcdDetected')?.value || false,
      lightExposureDetected:
        this.formGroup.get('lightExposureDetected')?.value || false,
      lightExposureDetectValue:
        this.formGroup.get('lightExposureDetectValue')?.value || 'continuous',
      shockDetected: this.formGroup.get('shockDetected')?.value || false,
      shockDetectedDetectValue:
        this.formGroup.get('shockDetectedDetectValue')?.value || 5,
      temperature: this.formGroup.get('temperature')?.value || false,
      temperatureSettings: [
        this.formGroup.get('temperatureSettings')?.get('temperatureAlertMax')
          ?.value || 0,
        this.formGroup.get('temperatureSettings')?.get('temperatureAlertMin')
          ?.value || 0,
        this.formGroup.get('temperatureSettings')?.get('temperaturePrealertMax')
          ?.value || 0,
        this.formGroup.get('temperatureSettings')?.get('temperaturePrealertMin')
          ?.value || 0,
      ],
      pressure: this.formGroup.get('pressure')?.value || false,
      pressureSettings: [
        this.formGroup.get('pressureSettings')?.get('pressureAlertMax')
          ?.value || 0,
        this.formGroup.get('pressureSettings')?.get('pressureAlertMin')
          ?.value || 0,
        this.formGroup.get('pressureSettings')?.get('pressurePrealertMax')
          ?.value || 0,
        this.formGroup.get('pressureSettings')?.get('pressurePrealertMin')
          ?.value || 0,
      ],
      humidity: this.formGroup.get('humidity')?.value || false,
      humiditySettings: [
        this.formGroup.get('humiditySettings')?.get('humidityAlertMax')
          ?.value || 0,
        this.formGroup.get('humiditySettings')?.get('humidityAlertMin')
          ?.value || 0,
        this.formGroup.get('humiditySettings')?.get('humidityPrealertMax')
          ?.value || 0,
        this.formGroup.get('humiditySettings')?.get('humidityPrealertMin')
          ?.value || 0,
      ],
      tilt: this.formGroup.get('tilt')?.value || false,
      tiltSettings: [
        this.formGroup.get('tiltSettings')?.get('tiltAlertMax')?.value || 0,
        this.formGroup.get('tiltSettings')?.get('tiltAlertMin')?.value || 0,
        this.formGroup.get('tiltSettings')?.get('tiltPrealertMax')?.value || 0,
        this.formGroup.get('tiltSettings')?.get('tiltPrealertMin')?.value || 0,
      ],
      light: this.formGroup.get('light')?.value || false,
      lightSettings: [
        this.formGroup.get('lightSettings')?.get('lightAlertMax')?.value || 0,
        this.formGroup.get('lightSettings')?.get('lightAlertMin')?.value || 0,
        this.formGroup.get('lightSettings')?.get('lightPrealertMax')?.value ||
          0,
        this.formGroup.get('lightSettings')?.get('lightPrealertMin')?.value ||
          0,
      ],
      fmd: this.formGroup.get('fmd')?.value || false,
      allExpired: false,
    };
    this.dialogRef.close(deviceSetting);
  }

  public selectAsset(assettypeId: string) {
    if (assettypeId) {
      const targetAssettype = this.assettypeList.filter(
        (asset) => asset['assettypeId'] == assettypeId,
      )[0];
      this.createForm(targetAssettype);
      // // トグルOff時の変更先の値を設定テンプレートものにしたいため、初期値を設定テンプレートの値に更新。
      this.assettypeRestoreValue = targetAssettype;
    } else {
      this.createForm(this.initDeviceGroupSetting);
      this.assettypeRestoreValue = this.initAssettype;
    }

    this.createFormGroup.subscribeFormChanges(this.formGroup);

    // 各センサーのトグル監視をまとめて登録（フォームが作り変えられるため再度実行）
    sensorsHavingForm.forEach((sensor) => this.watchSensorToggle(sensor));
  }

  private createForm(target: AssettypeViewModel | DeviceGroupSettingViewModel) {
    this.locationPriorities = this.createFormGroup.updateLocationPriorities(
      this.deviceType?.locationTechCellular,
      this.deviceType?.locationTechGnss,
      this.deviceType?.locationTechWifi,
    );

    this.locationPrioritiesNum = Object.keys(this.locationPriorities).length;

    this.formGroup = this.createFormGroup.createQTSFormGroup(
      target,
      this.deviceTypeQTS110,
    );

    this.formGroup.addControl(
      'shipmentTracking',
      new FormControl<boolean>(target?.shipmentTracking || false, {
        nonNullable: true,
      }),
    );

    // [#159]端末側の不具合のため、動作は設定不可とする
    this.formGroup.controls['mcdDetected'].setValue(false, {
      emitEvent: false,
    });
    this.formGroup.controls['mcdDetected'].disable();
  }

  /**
   * 送信間隔の選択肢が選択可能（非活性）か
   *
   * @param reportingFrequency 送信間隔の選択肢
   * @returns trueなら選択可能
   */
  public getDisabledReportingFrequency(reportingFrequency: number) {
    const samplingFrequency =
      this.formGroup.controls['samplingFrequency'].value;

    return this.createFormGroup.isDisabledReportingFrequency(
      samplingFrequency,
      reportingFrequency,
    );
  }

  public getLocationPriorityDisabled(
    value: string,
    compareValue1: string | null,
    compareValue2: string | null,
  ) {
    return this.createFormGroup.isLocationPriorityDisabled(
      value,
      compareValue1,
      compareValue2,
    );
  }

  /**
   * デバイス名編集アイコン選択時にフィールドの状態を変更する。
   */
  public enableDeviceSettingGroupNameEdit() {
    this.deviceSettingGroupNameEdit = true;
    this.changeDetectorRef.detectChanges();

    // バリデーションを削除する
    this.formGroup.clearValidators();
    this.formGroup.updateValueAndValidity();
    for (const formControlName in this.formGroup.controls) {
      this.formGroup.get(formControlName)?.clearValidators();
      this.formGroup.get(formControlName)?.updateValueAndValidity();
    }

    if (this.deviceSettingGroupNameEdit && this.deviceNameInputElement) {
      setTimeout(() => {
        this.deviceNameInputElement.nativeElement.focus();
      }, 0);
    }
  }

  /**
   * フォーカスアウト時にフィールドの状態を変更する。
   */
  public onBlur() {
    this.deviceSettingGroupNameEdit = false;
    this.deviceSettingGroupName =
      this.formGroup.get('deviceSettingGroupName')?.value ||
      this.deviceSettingGroupName;
  }

  /**
   * 送信間隔の値が送信間隔のリストに含まれているか判定する。
   */
  public reportingFrequencyValueChanged(): void {
    if (
      !this.reportingFrequencies.includes(
        this.formGroup.get('reportingFrequency')?.value,
      )
    ) {
      this.formGroup.controls['reportingFrequency'].setValue(null);
    }
  }

  /**
   * トグルの変更を監視して、トグルがOFFになったときに入力フォームを初期値に戻す
   */
  private watchSensorToggle(sensor: string): void {
    const control = this.formGroup.get(sensor);
    if (!control) {
      return;
    }

    control.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((val: boolean) => {
        // OFF (false) になったときにリセット
        if (val === false) {
          resetSensorSettings(
            this.formGroup,
            this.assettypeRestoreValue,
            sensor,
          );
        }
      });
  }
}
